import { msg } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { Ban, Check, ChevronRight } from 'lucide-react';
import { fetchChampionshipTeams } from 'modules/championship/store/championship/actions';
import { championshipSettingsSelector } from 'modules/championship/store/championship/selectors';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import { getApplicationLimitMessage, isApplicationLimitExceeded } from 'modules/teams/domain/utils/application-limit-error';
import { toastApplicationSaveError } from 'modules/teams/ui/utils';
import { announceNewTeam, upsertChampionshipTeamApplication } from 'modules/teams/store/championship-teams/actions';
import { useLazyGetTeamByIdQuery } from 'modules/teams/store/teams-api';
import { useUnclaimedTeams } from 'modules/teams/ui/hooks/use-unclaimed-teams';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import UDIconButton from 'ud-ui/components/icon-button';
import UDText from 'ud-ui/components/text';
import ChampionshipTeamCardHeader from '../team-card/championship-team-card-header';
import { CardContainer } from '../team-card/styles';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

interface UnclaimedTeamsListProps {
  championshipId: number;
  /** Команды, уже привязанные к этому турниру в любом статусе — их из выдачи исключаем. */
  excludeTeamIds: number[];
}

/**
 * Список команд организации, ещё не привязанных к турниру — показывается вместо
 * пустого состояния вкладки «Незаявленные», когда добавлять пока нечего.
 *
 * «Заявить»/«Отклонить» здесь сразу и привязывают команду к турниру, и решают её судьбу
 * целиком — быстрый путь, когда заявлять/отклонять по игрокам не нужно. Клик по самой
 * карточке — путь для точечного отбора: привязывает команду (как раньше делала
 * `AddTeamApplicationForm`) и сразу открывает её заявку модалкой, где игроков заявляют
 * по одному. Сам запрос шлёт китовая кнопка «Открыть заявку» карточки — карточка лишь нажимает её (`ref`),
 * поэтому двойной клик не привязывает команду дважды (IMGM-1561). Дата и число игроков берутся из того же `listTeams` — он отдаёт `createdAt` и
 * состав ровно настолько, чтобы посчитать реальных игроков (`isReal`); полные детали
 * (позиции, номера) при решении по команде всё равно дотягиваются отдельным запросом.
 */
const UnclaimedTeamsList = ({ championshipId, excludeTeamIds }: UnclaimedTeamsListProps) => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [fetchTeam] = useLazyGetTeamByIdQuery();
  // Не замок (нажатую кнопку запирает её промис), а «гасим соседние кнопки карточки», пока одна работает.
  const [actingTeamId, setActingTeamId] = useState<number | null>(null);
  const openButtons = useRef(new Map<number, HTMLButtonElement>());
  const settings = useSelector(championshipSettingsSelector(championshipId));
  const maxPlayers = settings?.applicationMaxPlayers ?? 28;

  const { availableTeams } = useUnclaimedTeams(excludeTeamIds);

  const handleDecide = useCallback(
    async (teamId: number, status: AnnouncedTeamStatus) => {
      setActingTeamId(teamId);

      try {
        const detailTeam = await fetchTeam(teamId).unwrap();

        if (!detailTeam) {
          toast.error(t`Команда не найдена`);
          return;
        }

        const players = detailTeam.players.filter((player) => !player.isReal);

        if (players.length === 0) {
          toast.error(t`Необходимо добавить участников в команду.`);
          return;
        }

        if (status === AnnouncedTeamStatus.ACCEPTED && players.length > maxPlayers) {
          toast.error(
            t`Превышено допустимое количество игроков в команде. По условиям турнира максимальное количество игроков в команде: ${maxPlayers}. При заявлении команды все игроки, находящиеся в ней, будут заявлены. Для заявления конкретных игроков перейдите в заявку команды, кликнув на неё.`,
            { autoClose: 1000 * 45 },
          );
          return;
        }

        await dispatch(
          announceNewTeam({
            team: { id: detailTeam.id, name: detailTeam.name, shortName: detailTeam.shortName, logo: detailTeam.logo, players },
            championshipId,
          }),
        ).unwrap();

        await dispatch(upsertChampionshipTeamApplication({ teamId, status, championshipId })).unwrap();

        toast.success(
          status === AnnouncedTeamStatus.ACCEPTED
            ? t`Все игроки команды успешно заявлены.`
            : t`Все заявки игроков команда успешно отклонены.`,
        );
        dispatch(fetchChampionshipTeams(championshipId));
      } catch (error) {
        if (isApplicationLimitExceeded(error)) {
          toast.error(getApplicationLimitMessage(maxPlayers, 'team'), { autoClose: 1000 * 45 });
        } else {
          // Отказ 7016 приходит уже после добавления команды: команда в соревновании,
          // а её игрока не заявили — бэкенд называет, где он уже заявлен.
          toastApplicationSaveError(error, msg`Не удалось добавить команду`);
        }
      } finally {
        setActingTeamId(null);
      }
    },
    [championshipId, dispatch, fetchTeam, maxPlayers, t],
  );

  const handleCardClick = useCallback(
    async (teamId: number) => {
      setActingTeamId(teamId);

      try {
        const detailTeam = await fetchTeam(teamId).unwrap();

        if (!detailTeam) {
          toast.error(t`Команда не найдена`);
          return;
        }

        await dispatch(
          announceNewTeam({
            team: {
              id: detailTeam.id,
              name: detailTeam.name,
              shortName: detailTeam.shortName,
              logo: detailTeam.logo,
              players: detailTeam.players.filter((player) => !player.isReal),
            },
            championshipId,
          }),
        ).unwrap();

        navigate(String(teamId));
      } catch (error) {
        toastApiError(error, msg`Не удалось добавить команду`);
      } finally {
        setActingTeamId(null);
      }
    },
    [championshipId, dispatch, fetchTeam, navigate, t, toastApiError],
  );

  if (availableTeams.length === 0) {
    return (
      <div className="flex-center h-full">
        <UDText type="body-text" className="text-surfaceRomanSilver30">
          <Trans>Список пуст</Trans>
        </UDText>
      </div>
    );
  }

  return (
    <div>
      {availableTeams.map((team) => {
        const isActing = actingTeamId === team.id;
        // Имя `count` — общее с карточками заявок: одинаковый плюрал даёт один msgid в каталоге.
        const count = team.players.filter((player) => !player.isReal).length;
        // `team.createdAt` уже отформатирован в дату-строку мапером (`getNormalizedDate`),
        // а не ISO-таймстамп — повторный `new Date(...)` + `format` на нём падал с
        // «Invalid time value». Показываем как есть, с сегодняшней датой как запасной.
        const teamDate = team.createdAt || format(new Date(), 'dd.MM.yyyy');

        return (
          <CardContainer
            key={team.id}
            className="mb-2 last:mb-0"
            style={{ cursor: 'pointer' }}
            onClick={() => openButtons.current.get(team.id)?.click()}
          >
            <ChampionshipTeamCardHeader
              isExpanded={false}
              teamName={team.name}
              shortName={team.shortName}
              teamLogo={team.logo?.url}
              actions={
                <div className="flex items-center justify-between" style={{ flexBasis: '50%' }}>
                  <UDText type="subhead" style={{ flexBasis: '30%' }}>
                    {teamDate}
                  </UDText>
                  <div className="flex grow items-center justify-between">
                    <UDText type="subhead" className="team-players-count">
                      <Plural value={count} one="# игрок" few="# игрока" many="# игроков" other="# игрока" />
                    </UDText>
                    <div className="interactive flex items-center justify-end" style={{ width: 148, minWidth: 148, gap: 20 }}>
                      <UDIconButton
                        icon={<Check />}
                        variant="success"
                        tooltip={t`Заявить`}
                        aria-label={t`Заявить`}
                        disabled={isActing}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          return handleDecide(team.id, AnnouncedTeamStatus.ACCEPTED);
                        }}
                      />
                      <UDIconButton
                        icon={<Ban />}
                        variant="danger"
                        tooltip={t`Отклонить`}
                        aria-label={t`Отклонить`}
                        disabled={isActing}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          return handleDecide(team.id, AnnouncedTeamStatus.DECLINED);
                        }}
                      />
                      <UDIconButton
                        ref={(node) => {
                          if (node) openButtons.current.set(team.id, node);
                          else openButtons.current.delete(team.id);
                        }}
                        icon={<ChevronRight />}
                        tooltip={t`Открыть заявку`}
                        aria-label={t`Открыть заявку`}
                        disabled={isActing}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          return handleCardClick(team.id);
                        }}
                      />
                    </div>
                  </div>
                </div>
              }
            />
          </CardContainer>
        );
      })}
    </div>
  );
};

export default UnclaimedTeamsList;
